import { useMemo, useState, useRef } from 'react';
import { type Dog } from '@/api/entities/dogs';
import { useDebounce } from '@/hooks/useDebounce';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { EditDogDialog } from './edit-dialog';
import { AddDogDialog } from './add-dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useDogs } from './api/useDogs';
import { useDeleteDog } from './api/useDeleteDog';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Trash2, Filter, X } from 'lucide-react';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDateDisplay } from '@/utils/date';
import { TableEmptyState } from '@/components/ui/empty-state';

type SortConfig = {
  key: keyof Dog | null;
  direction: 'asc' | 'desc';
};

export function DogsList() {
  const [editingDog, setEditingDog] = useState<Dog | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deletingDog, setDeletingDog] = useState<Dog | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [selectedDogs, setSelectedDogs] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useLocalStorage<SortConfig>('dogs-sort-config', {
    key: null,
    direction: 'asc',
  });
  const [filters, setFilters] = useLocalStorage<{ category?: string; sex?: string }>(
    'dogs-filters',
    {},
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcuts
  useKeyboardShortcuts(
    [
      {
        key: 'k',
        ctrlKey: true,
        handler: () => {
          searchInputRef.current?.focus();
        },
        description: 'Focus search',
      },
      {
        key: 'k',
        metaKey: true,
        handler: () => {
          searchInputRef.current?.focus();
        },
        description: 'Focus search',
      },
      {
        key: '/',
        handler: () => {
          searchInputRef.current?.focus();
        },
        description: 'Focus search',
      },
      {
        key: 'n',
        ctrlKey: true,
        handler: () => {
          setIsAddDialogOpen(true);
        },
        description: 'New dog',
      },
      {
        key: 'n',
        metaKey: true,
        handler: () => {
          setIsAddDialogOpen(true);
        },
        description: 'New dog',
      },
      {
        key: 'Escape',
        handler: () => {
          if (selectedDogs.size > 0) {
            setSelectedDogs(new Set());
          } else if (searchQuery || filters.category || filters.sex) {
            setSearchQuery('');
            setFilters({});
          }
        },
        description: 'Clear selection/filters',
      },
    ],
    true,
  );

  const { data, isLoading, isError, error, refetch } = useDogs({
    config: {
      staleTime: 30_000,
    },
  });

  const deleteMutation = useDeleteDog({
    config: {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'Dog deleted successfully',
        });
        setDeletingDog(null);
      },
      onError: (err: Error) => {
        toast({
          title: 'Error',
          description: err.message || 'Failed to delete dog',
          variant: 'destructive',
        });
      },
    },
  });

  const filteredAndSortedDogs = useMemo(() => {
    if (!data) return [];
    let dogs = [...(data || [])] as Dog[];

    // Apply advanced filters
    if (filters.category) {
      dogs = dogs.filter((dog) => dog.category === filters.category);
    }
    if (filters.sex) {
      dogs = dogs.filter((dog) => dog.sex === filters.sex);
    }

    // Apply search filter (debounced)
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      dogs = dogs.filter(
        (dog) =>
          dog.name?.toLowerCase().includes(query) ||
          dog.longName?.toLowerCase().includes(query) ||
          dog.category?.toLowerCase().includes(query) ||
          dog.sex?.toLowerCase().includes(query) ||
          dog.color?.toLowerCase().includes(query) ||
          dog.akc?.toLowerCase().includes(query) ||
          dog.ownedBy?.toLowerCase().includes(query),
      );
    }

    // Apply sorting
    if (sortConfig.key === null) return dogs;

    return dogs.sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle date sorting
      if (sortConfig.key === 'dob') {
        const aDate = a.dob ? new Date(a.dob).getTime() : 0;
        const bDate = b.dob ? new Date(b.dob).getTime() : 0;
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle string sorting
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, debouncedSearchQuery, filters]);

  const handleEditClick = (dog: Dog) => {
    setEditingDog(dog);
    setIsEditDialogOpen(true);
  };

  const handleSort = (key: keyof Dog) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDogs(new Set(filteredAndSortedDogs.map((dog) => dog.id)));
    } else {
      setSelectedDogs(new Set());
    }
  };

  const handleSelectDog = (dogId: string, checked: boolean) => {
    const newSelected = new Set(selectedDogs);
    if (checked) {
      newSelected.add(dogId);
    } else {
      newSelected.delete(dogId);
    }
    setSelectedDogs(newSelected);
  };

  const handleBulkDelete = async () => {
    const dogsToDelete = filteredAndSortedDogs.filter((dog) => selectedDogs.has(dog.id));
    try {
      await Promise.all(dogsToDelete.map((dog) => deleteMutation.mutateAsync(dog)));
      toast({
        title: 'Success',
        description: `Successfully deleted ${dogsToDelete.length} dog${dogsToDelete.length > 1 ? 's' : ''}`,
      });
      setSelectedDogs(new Set());
      setIsBulkDeleteDialogOpen(false);
    } catch (deleteError) {
      toast({
        title: 'Error',
        description: 'Failed to delete some dogs',
        variant: 'destructive',
      });
    }
  };

  const isAllSelected =
    filteredAndSortedDogs.length > 0 && selectedDogs.size === filteredAndSortedDogs.length;
  const isSomeSelected = selectedDogs.size > 0 && selectedDogs.size < filteredAndSortedDogs.length;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-9 w-32 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
        <div className="rounded-lg border bg-card shadow-sm p-4">
          <div className="grid grid-cols-6 gap-4 mb-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dogs</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your dog records and information
            </p>
          </div>
        </div>
        <div className="rounded-lg border bg-card shadow-sm p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-sm font-medium text-destructive mb-2">Failed to load dogs</p>
            <pre className="mt-2 text-xs text-muted-foreground mb-4 max-w-md">
              {(error as Error)?.message}
            </pre>
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  const getSortIcon = (columnKey: keyof Dog) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dogs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your dog records and information
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>New Dog</Button>
      </div>
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 max-w-sm">
            <label htmlFor="dog-search" className="sr-only">
              Search dogs
            </label>
            <InputGroup>
              <InputGroupInput
                ref={searchInputRef}
                id="dog-search"
                placeholder="Search dogs... (Ctrl+K or /)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search dogs"
              />
              <InputGroupAddon>
                <Search className="h-4 w-4" aria-hidden="true" />
              </InputGroupAddon>
            </InputGroup>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={filters.category || 'all'}
              onValueChange={(value) =>
                setFilters({ ...filters, category: value === 'all' ? undefined : value })
              }
            >
              <SelectTrigger className="w-[140px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="stud">Stud</SelectItem>
                <SelectItem value="brood">Brood</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.sex || 'all'}
              onValueChange={(value) =>
                setFilters({ ...filters, sex: value === 'all' ? undefined : value })
              }
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Sex" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sex</SelectItem>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
              </SelectContent>
            </Select>
            {(filters.category || filters.sex) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({})}
                aria-label="Clear filters"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {selectedDogs.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{selectedDogs.size} selected</span>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsBulkDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          )}
        </div>
        {(filters.category || filters.sex) && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Active filters:</span>
            {filters.category && (
              <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium">
                Category: {filters.category}
              </span>
            )}
            {filters.sex && (
              <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium">
                Sex: {filters.sex}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="pl-4">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-12 w-12">
                  <Checkbox
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = isSomeSelected;
                      }
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    aria-label="Select all dogs"
                  />
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('name')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('name');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Name ${sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'name'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Name
                    {getSortIcon('name')}
                  </button>
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('category')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('category');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Category ${sortConfig.key === 'category' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'category'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Category
                    {getSortIcon('category')}
                  </button>
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('sex')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('sex');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Sex ${sortConfig.key === 'sex' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'sex'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Sex
                    {getSortIcon('sex')}
                  </button>
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('color')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('color');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Color ${sortConfig.key === 'color' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'color'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Color
                    {getSortIcon('color')}
                  </button>
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('dob')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('dob');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Date of Birth ${sortConfig.key === 'dob' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'dob'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Date of Birth
                    {getSortIcon('dob')}
                  </button>
                </TableHead>
                <TableHead className="h-12 w-[140px] text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedDogs.map((dog) => (
                <TableRow
                  key={dog.id}
                  className={`hover:bg-muted/50 ${selectedDogs.has(dog.id) ? 'bg-muted' : ''}`}
                >
                  <TableCell className="py-4">
                    <Checkbox
                      checked={selectedDogs.has(dog.id)}
                      onChange={(e) => handleSelectDog(dog.id, e.target.checked)}
                      aria-label={`Select ${dog.name}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium py-4">
                    <div>
                      <div>{dog.name}</div>
                      {dog.longName && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {dog.longName}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium capitalize text-muted-foreground">
                      {dog.category || '-'}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="capitalize text-muted-foreground">{dog.sex || '-'}</span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="capitalize text-muted-foreground">{dog.color || '-'}</span>
                  </TableCell>
                  <TableCell className="py-4 text-muted-foreground">
                    {formatDateDisplay(dog.dob)}
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(dog)}
                        aria-label={`Edit ${dog.name}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setDeletingDog(dog);
                          setIsDeleteDialogOpen(true);
                        }}
                        aria-label={`Delete ${dog.name}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAndSortedDogs.length === 0 && (
                <TableEmptyState
                  title={searchQuery ? 'No dogs found' : 'No dogs yet'}
                  description={
                    searchQuery
                      ? 'Try adjusting your search query'
                      : 'Get started by creating your first dog record'
                  }
                  action={
                    !searchQuery
                      ? {
                          label: 'Add Dog',
                          onClick: () => setIsAddDialogOpen(true),
                        }
                      : undefined
                  }
                  colSpan={7}
                />
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <EditDogDialog dog={editingDog} open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} />
      <AddDogDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} />
      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={() => {
          if (deletingDog) {
            deleteMutation.mutate(deletingDog);
          }
        }}
        title="Delete Dog"
        description={`Are you sure you want to delete "${deletingDog?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
      <ConfirmDialog
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        onConfirm={handleBulkDelete}
        title="Delete Selected Dogs"
        description={`Are you sure you want to delete ${selectedDogs.size} dog${selectedDogs.size > 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
    </div>
  );
}
